import * as React from "react";
import { CheckCircle2, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { usePortfolioConsultation } from "@/hooks/useCheckout";
import { ApiError } from "@/lib/api";

interface ConsultationDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  planSlug?: string;
}

/**
 * Modal form for Portfolio Custom enquiries. Posts to
 * /api/checkout/portfolio-consultation. On success shows a thank-you
 * screen; the modal stays open so the user gets visible confirmation.
 */
export function PortfolioConsultationDialog({
  open,
  onOpenChange,
  planSlug = "portfolio-custom"
}: ConsultationDialogProps) {
  const [fullName, setFullName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [companyName, setCompanyName] = React.useState("");
  const [domain, setDomain] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string[]>>({});

  const { toast } = useToast();
  const consultation = usePortfolioConsultation();

  function reset() {
    setFullName("");
    setEmail("");
    setPhone("");
    setCompanyName("");
    setDomain("");
    setNotes("");
    setSubmitted(false);
    setFieldErrors({});
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});
    try {
      await consultation.mutateAsync({
        full_name: fullName.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        company_name: companyName.trim() || undefined,
        domain: domain.trim() || undefined,
        notes: notes.trim() || undefined,
        plan_slug: planSlug
      });
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError && err.body && typeof err.body === "object") {
        const body = err.body as { errors?: Record<string, string[]>; message?: string };
        if (body.errors) setFieldErrors(body.errors);
        toast({
          title: "Couldn't send",
          description: body.message ?? err.message,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Couldn't send",
          description: err instanceof Error ? err.message : "Try again.",
          variant: "destructive"
        });
      }
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogContent className="max-w-xl">
        {submitted ? (
          <ThankYouView onDone={() => onOpenChange(false)} />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Talk to sales about Portfolio Custom</DialogTitle>
              <DialogDescription>
                Tell us a bit about your portfolio. We'll get back within 24 hours with a tailored quote.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  id="fullName"
                  label="Full name"
                  required
                  autoComplete="name"
                  value={fullName}
                  onChange={setFullName}
                  errors={pickErrors(fieldErrors, "Request.FullName", "full_name")}
                />
                <FormField
                  id="email"
                  label="Email"
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={setEmail}
                  errors={pickErrors(fieldErrors, "Request.Email", "email")}
                />
                <FormField
                  id="phone"
                  label="Phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={setPhone}
                  errors={pickErrors(fieldErrors, "Request.Phone", "phone")}
                />
                <FormField
                  id="companyName"
                  label="Company"
                  autoComplete="organization"
                  value={companyName}
                  onChange={setCompanyName}
                  errors={pickErrors(fieldErrors, "Request.CompanyName", "company_name")}
                />
              </div>

              <FormField
                id="domain"
                label="Primary domain (optional)"
                placeholder="example.com"
                value={domain}
                onChange={setDomain}
                errors={pickErrors(fieldErrors, "Request.DomainHint", "domain")}
              />

              <div className="space-y-2">
                <Label htmlFor="notes">Anything else?</Label>
                <textarea
                  id="notes"
                  rows={4}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  placeholder="How many domains, current registrar, traffic level, must-have features…"
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={consultation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={consultation.isPending || !fullName.trim() || !email.trim()}
                >
                  {consultation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Request consultation
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ThankYouView({ onDone }: { onDone: () => void }) {
  return (
    <div className="flex flex-col items-center gap-4 py-4 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-greenSoft text-brand-green">
        <CheckCircle2 className="h-7 w-7" />
      </div>
      <DialogTitle className="text-xl">Request received</DialogTitle>
      <DialogDescription className="max-w-sm">
        We'll be in touch within 24 hours. In the meantime, feel free to explore the standard plans
        — you can upgrade or cancel anytime.
      </DialogDescription>
      <Button onClick={onDone} className="mt-2">Got it</Button>
    </div>
  );
}

interface FormFieldProps {
  id: string;
  label: string;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  errors?: string[];
}

function FormField({ id, label, type = "text", required, autoComplete, inputMode, value, onChange, placeholder, errors }: FormFieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        required={required}
        autoComplete={autoComplete}
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        aria-invalid={errors && errors.length > 0}
      />
      {errors && errors.length > 0 && (
        <p className="text-xs text-destructive">{errors[0]}</p>
      )}
    </div>
  );
}

function pickErrors(errors: Record<string, string[]>, ...keys: string[]): string[] | undefined {
  for (const k of keys) if (errors[k]) return errors[k];
  return undefined;
}
